import React, {
  useEffect,
  useState,
  useRef,
  type ChangeEvent,
  type FC,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
  type PropsWithChildren,
  type RefAttributes,
} from 'react';

import kebabCase from 'lodash/kebabCase';
import { useRecaptcha, useSmartlingStrings } from '@lucid/hooks';
import {
  sendBeaconEvent,
  sendGoogleEvent,
  isBrowser,
  hasHTMLTag,
  filterMarketoError,
  splunkLogger,
  workato,
  getLanguage,
  withCookie,
} from '@lucid/utils';
import type { ValidationErrors, FormApi } from 'final-form';
import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import qs from 'query-string';
import axios, { type AxiosResponse } from 'axios';
import startCase from 'lodash/startCase';

import reactHTMLParser from '../../utils/reactHTMLParser';
import Icon from '../icon/icon';
import Box from '../box/box';
import Link from '../link/link';
import Text from '../text/text';
import CheckboxField from '../form-fields/checkbox/checkbox';
import CheckboxGroup from '../form-fields/checkbox/checkbox-group';
import DateField from '../form-fields/date/date';
import DropdownField from '../form-fields/dropdown/dropdown';
import InputField from '../form-fields/input/input';
import RadioButtonField from '../form-fields/radio-button/radio-button';
import RadioGroup from '../form-fields/radio-button/radio-group';
import Fieldset from './generic-form-elements/fieldset';
import Legend from './generic-form-elements/legend';
import {
  composeValidators,
  isRequired,
  isSelectRequired,
  isValidEmail,
  isValidEnterpriseEmail,
  isValidPhoneNumber,
} from './validation/validation';
import type { Lucid } from '../../types/types';
import Heading from '../heading/heading';
import AccessibleDescription from '../accessible-description/accessible-description';

export interface MarketoFormProps extends RefAttributes<HTMLFormElement> {
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  confirm?: {
    heading?: string;
    body?: string;
  };
  /** The marketo form data. */ // TODO: remove isOneColumnLayout after AB Test has run
  form?: Lucid.Marketo.FormInterface & { isOneColumnLayout?: boolean };
  /** Determines whether the text should be light or dark */
  hasDarkBackground?: boolean;
  /** Checks if the submit button should be full width of form. */
  hasFullWidthSubmit?: boolean;
  /** Checks if the top text should be hidden. */
  shouldHideTopText?: boolean;
  languageKey?: Lucid.I18n.LanguagesShortCode;
  /** Determines whether to use Dark Mode styles */
  isDarkMode?: boolean;
  /** Checks if the form elements should be full width. */
  isFullWidth?: boolean;
  /** Determines if a custom confirm message should only be used */
  shouldOnlyUseChildren?: boolean;
  location: {
    pathname?: string;
    origin?: string;
    search?: string;
  };
  onSuccess?: () => void;
  /** The current site implementing the form. */
  product?: Lucid.Product.Sites;
  /** Checks if a checkbox dataType on a form should require at least one selected to submit. */
  shouldRequireAtLeastOneCheckbox?: boolean;
  submitText?: string | null;
  topText?: {
    title?: string;
    body?: string;
    note?: string;
  };
  /** Checks if the form will show terms verbiage. */
  willShowTerms?: boolean;
  /** Determines what type of form this is to send to the Beacon Event */
  beaconFormType?: Lucid.Marketo.BeaconFormTypes;
}

interface FieldGroups {
  /** The title for a group of fields in a fieldset. */
  fieldsetTitle?: string | null;
  /** The marketo form fields. */
  fields: Lucid.Marketo.ChildrenFields[];
  /** Checks if we should render grouped fields with a title. */
  willRenderFieldsetTitle?: boolean;
  /** Checks if we should render grouped fields inside their own fieldset. */
  willRenderGroupedFieldsInsideFieldset?: boolean;
}

interface GoogleAnalyticsData {
  marketo_formId?: string | null;
  marketo_formName?: string | null;
  marketo_formType: string;
  marketo_formProduct: string;
}

interface GetDefaultValues {
  validDataTypes: Lucid.Marketo.DataType[];
  formChildren?: Lucid.Marketo.ChildrenFields[];
  googleAnalyticsData?: GoogleAnalyticsData;
  setGoogleAnalyticsData: Dispatch<SetStateAction<GoogleAnalyticsData>>;
  parsedQueryString: qs.ParsedQuery<string>;
}

type FieldTypeLookupKeys = 'email' | 'number' | 'telephone' | 'text';

type FieldTypeLookup = {
  [K in FieldTypeLookupKeys]: string;
};

const typeLookup: FieldTypeLookup = {
  text: 'text',
  telephone: 'tel',
  email: 'email',
  number: 'number',
};

// Visitor Fields defined in Qualified
const qualifiedFields = [
  'email',
  'company',
  'conversion_page_url',
  'country_code',
  'department',
  'description',
  'employees',
  'industry',
  'lead_source',
  'name',
  'phone',
  'status',
  'title',
] as const;

type QualifiedFieldsType = keyof typeof qualifiedFields;
type QualifiedVisitorObjectType = Record<QualifiedFieldsType, string>;

const formFieldGroup = (
  validDataTypes: Lucid.Marketo.DataType[],
  formChildren?: Lucid.Marketo.ChildrenFields[],
): FieldGroups[] => {
  // fieldsets are added after the fields they contain from marketo form
  // so we reduceRight to get the fieldsets and group their children within
  if (!formChildren) return [];

  const fieldGroups = formChildren
    .reduceRight<FieldGroups[]>((previous, child) => {
      const currentChildDataType = child.dataType as Lucid.Marketo.DataType;

      // checks if the dataType is a fieldset where will need to create a new group
      if (currentChildDataType === 'fieldset') {
        // if the previous array item doesn't have a fieldsetTitle
        // return the current child with a fieldsetTitle,
        // allowing for all other fields to be group together
        if (!previous[0]?.fieldsetTitle) {
          return [
            ...previous,
            {
              fieldsetTitle: child.label,
              fields: [],
              willRenderFieldsetTitle: true,
              willRenderGroupedFieldsInsideFieldset: true,
            },
          ];
        }

        return previous;
      }

      if (validDataTypes.includes(currentChildDataType)) {
        // we find the current index in the previous array,
        // and check that it is not an empty array
        // we then get the index of the last item in the array
        // which would be the current working fieldset for this child
        const currentGroupIndex = previous.length ? previous.length - 1 : 0;
        // we get the current fieldset container
        const currentGroup = previous[currentGroupIndex] as FieldGroups;

        // if the current fieldset container has a fieldsetTitle
        // we will add the new child fields within this array
        if (currentGroup?.fieldsetTitle || currentGroup?.fields.find(field => field.rowNumber === child.rowNumber)) {
          // we splice out the current fieldset container for the rest
          // of the previous array, so we do not spread duplicate entries
          // into the array values
          const previousGroupRemaining = previous.splice(0, currentGroupIndex);

          // we must return child first since we used reduceRight
          return [
            ...previousGroupRemaining,
            {
              ...currentGroup,
              fields: [child, ...currentGroup.fields],
            },
          ];
        }

        // if there is no fieldsetTitle we assume it is a
        // single non-group form element that we will return
        return [
          ...previous,
          {
            ...currentGroup,
            fieldsetTitle: undefined,
            fields: [child],
          },
        ];
      }

      return previous;
    }, [])
    .reverse();

  // since we used reduceRight we have to then reverse the array
  // to get the correct order of the fieldsets and fields that
  // will match the visual output shown within marketo
  return fieldGroups;
};

const getDefaultValues = ({
  validDataTypes,
  formChildren,
  googleAnalyticsData,
  setGoogleAnalyticsData,
  parsedQueryString,
}: GetDefaultValues) => {
  const fieldGroups = formFieldGroup(validDataTypes, formChildren);
  const allFieldGroups = fieldGroups?.map(fGroup =>
    fGroup?.fields.reduce<Record<string, Lucid.Marketo.FormFieldStateValues>>((previous, field) => {
      let defaultValue: Lucid.Marketo.FormFieldStateValues = field.defaultValue || '';
      const isCheckbox = field.dataType === 'checkbox';
      const isCheckboxes = field.dataType === 'checkboxes';
      const fieldMetaDataValues = field?.fieldMetaData?.values;
      const fieldAutoFillValue = field?.autoFill?.value || '';

      if (field.formPrefill) {
        if (fieldMetaDataValues) {
          const isCheckboxInitiallyChecked = !!field?.fieldMetaData?.initiallyChecked || false;
          const selectedValue = fieldMetaDataValues.filter(data => data.selected);
          const currentlySelectedValue = selectedValue[0]?.value || '';
          const currentlySelectedMetaDataValue = fieldMetaDataValues[0]?.value || '';

          defaultValue = currentlySelectedValue || currentlySelectedMetaDataValue;

          if (isCheckbox || isCheckboxes) {
            defaultValue = isCheckboxInitiallyChecked ? [currentlySelectedValue] : [currentlySelectedMetaDataValue];
          }

          if (field.dataType === 'select') {
            defaultValue = selectedValue && selectedValue[0] ? selectedValue[0].value || '' : '';
          }
        }

        if (fieldAutoFillValue) {
          defaultValue = [fieldAutoFillValue];
        }

        // if the form should be prefilled, but there are no values provided by marketo,
        // we will replace the empty string with a string of true
        if (!fieldMetaDataValues && isCheckbox) {
          defaultValue = 'true';
        }
      }

      // check all other fields to see if we have a default value in the query string
      if (field?.marketoId in parsedQueryString) {
        defaultValue = (parsedQueryString[field.marketoId] as string) || '';
      }

      if (field.dataType === 'hidden') {
        const {
          value = '',
          valueFrom = '',
          parameterName = '',
        } = field.autoFill || {
          value: '',
          valueFrom: '',
          parameterName: '',
        };

        if (field.apiId === 'formType' && !googleAnalyticsData?.marketo_formType) {
          setGoogleAnalyticsData(prevState => ({
            ...prevState,
            marketo_formType: value,
          }));
        }

        if (field.apiId === 'formProduct' && !googleAnalyticsData?.marketo_formProduct) {
          setGoogleAnalyticsData(prevState => ({
            ...prevState,
            marketo_formProduct: value,
          }));
        }

        if (valueFrom === 'default') {
          defaultValue = value;
        }

        if (valueFrom === 'cookie') {
          defaultValue = withCookie.get(parameterName) || '';
        }

        if (valueFrom === 'query') {
          defaultValue = (parsedQueryString[parameterName] as string) || '';
        }
      }

      return {
        ...previous,
        [field.marketoId]: defaultValue,
      };
    }, {}),
  );

  const formattedDefaultFieldValues = allFieldGroups.reduce(
    (previous, current) => ({
      ...previous,
      ...current,
    }),
    {},
  );

  return formattedDefaultFieldValues;
};

const getRequiredCheckboxes = (fields?: Lucid.Marketo.ChildrenFields[], willRequireAtLeastOneCheckbox = false) => {
  if (!willRequireAtLeastOneCheckbox) return [];

  const requiredCheckboxes = fields?.reduce<string[]>((previous, field) => {
    if (field.dataType === 'checkbox') {
      return [...previous, field.marketoId];
    }

    return previous;
  }, []);

  return requiredCheckboxes || [];
};

const FormStarted = ({
  beaconEventData,
  googleAnalyticsData,
  isDirty,
}: {
  beaconEventData: Lucid.Marketo.BeaconEventData;
  googleAnalyticsData: GoogleAnalyticsData;
  isDirty: boolean;
}) => {
  const [hasFormStarted, setHasFormStarted] = useState(false);

  useEffect(() => {
    if (!hasFormStarted && isDirty && isBrowser()) {
      sendBeaconEvent({ ...beaconEventData });

      sendGoogleEvent({
        name: 'mktoFormStarted',
        data: googleAnalyticsData,
      });
      setHasFormStarted(true);
    }
  }, [isDirty, hasFormStarted, googleAnalyticsData, beaconEventData]);

  return null;
};

const MarketoForm: FC<PropsWithChildren<MarketoFormProps>> = ({
  beaconFormType,
  children,
  className,
  confirm = {},
  form,
  shouldHideTopText = false,
  hasDarkBackground = false,
  hasFullWidthSubmit = false,
  languageKey = 'en',
  isDarkMode = false,
  isFullWidth = false,
  location,
  onSuccess,
  product,
  shouldOnlyUseChildren = false,
  shouldRequireAtLeastOneCheckbox = false,
  submitText = null,
  topText = {},
  willShowTerms = true,
  ...rest
}) => {
  const validDataTypes: Lucid.Marketo.DataType[] = [
    'currency',
    'checkbox',
    'checkboxes',
    'date',
    'email',
    'hidden',
    'number',
    'radioButtons',
    'select',
    'telephone',
    'text',
    'textArea',
  ];
  const formElementRef = useRef<HTMLFormElement>(null);
  const formInputRef = useRef<Array<HTMLInputElement | HTMLTextAreaElement>>([]);
  const alertRef = useRef<HTMLInputElement>(null);
  const [hasFormError, _setHasFormError] = useState(false);
  const [hasFormSent, setHasFormSent] = useState(false);
  const [hasWorkatoError, setHasWorkatoError] = useState(false);
  const formId = form?.marketoId;
  const formName = form?.name;
  const sessionKey = `mkto-${formId}`;
  const isSalesForm = !!form?.childrenMarketoFormField.find(field => field.marketoId === 'LeadSource');
  const parsedQueryString = qs.parse(location?.search || '');
  const withSmartling = useSmartlingStrings({
    category: 'forms',
    shortCode: languageKey,
  });

  const languageName = getLanguage(languageKey).name;
  const marketoMunchkinTracker = withCookie.get('_mkto_trk');
  const userId = withCookie.get('userId');
  const { hasRecaptchaLoaded, getToken } = useRecaptcha();
  const textAreaIds: string[] = [];
  const { title, body, note } = topText || {};
  const overrideTopText = shouldHideTopText || title || body || note;
  const shouldTriggerQualified = isBrowser() && 'qualified' in window && formId === '13';

  if (!overrideTopText && !validDataTypes.includes('htmltext')) {
    validDataTypes.push('htmltext');
  }

  // AB Test Var
  const isOneColumnLayout = form?.isOneColumnLayout;

  useEffect(() => {
    if (hasWorkatoError) {
      alertRef?.current?.focus();
    }
  }, [hasWorkatoError]);

  // State to store Google Analytics data
  const [googleAnalyticsData, setGoogleAnalyticsData] = useState<GoogleAnalyticsData>({
    marketo_formId: formId,
    marketo_formName: formName,
    marketo_formType: '',
    marketo_formProduct: '',
  });
  let sessionValues: Record<string, string> | null = null;

  if (isBrowser()) {
    try {
      // Try-Catch incase the session storage returns malformed data
      sessionValues = JSON.parse(sessionStorage.getItem(sessionKey) || '');
    } catch {
      // no-op
    }
  }
  const requiredCheckboxList = getRequiredCheckboxes(form?.childrenMarketoFormField, shouldRequireAtLeastOneCheckbox);
  const fieldGroups = formFieldGroup(validDataTypes, form?.childrenMarketoFormField);
  const formDefaultValues = getDefaultValues({
    validDataTypes,
    formChildren: form?.childrenMarketoFormField,
    googleAnalyticsData,
    setGoogleAnalyticsData,
    parsedQueryString,
  });

  const getBeaconFormProduct = (product?: Lucid.Product.Sites) => {
    switch (product) {
      case 'co':
        return 'suite';
      case 'edu':
        return 'l4e';
      case 'spark':
        return 'lucidspark';
      case 'scale':
        return 'lucidscale';
      default:
        return 'chart';
    }
  };

  const getFormUrl = () => {
    if (isBrowser()) return window.location.href;

    return 'undefined';
  };

  const initialValues = sessionValues || formDefaultValues;

  useEffect(() => {
    sendBeaconEvent({
      anonymousId: withCookie.get('lt-anonymous-id') || '',
      formId,
      formName: formName || '',
      formProduct: getBeaconFormProduct(product),
      formType: beaconFormType || 'sales',
      name: 'openedMarketoForm',
      previousPageviewId: sessionStorage.getItem('previousPageviewId') || null,
      userId: Number(userId) || -1,
    });

    // adding the ability to set a queryParam to skip form submission
    // and just show the thank you message and resources unlocked by
    // form submission if any
    if (parsedQueryString.skipForm === 'true') {
      setHasFormSent(true);
    }
  }, [formId, formName, userId, parsedQueryString.skipForm, beaconFormType, product]);

  if (!formId) return null;

  if (hasFormSent) {
    if (onSuccess && parsedQueryString.skipForm !== 'true') {
      onSuccess();
    }

    if (shouldTriggerQualified) {
      // Hard-coding Experience ID for MVP
      // Further customization will require us to get this dynamically
      window.qualified('showFormExperience', 'experience-1701108366067');

      let iterations = 0;

      const checkForQualified = (interval: NodeJS.Timeout) => {
        const chatbot = document?.querySelector('#q-messenger-frame');

        if (chatbot) {
          (chatbot as HTMLElement).focus();
        }

        if (iterations > 5 || chatbot) {
          return clearInterval(interval);
        }

        iterations++;
      };

      const interval = setInterval(() => checkForQualified(interval), 1000);
    }

    return (
      <React.Fragment key="marketo-form-sent">
        {!shouldOnlyUseChildren && (
          <Box css={{ label: 'formSent' }} data-testid="defaultMessageContainer">
            <Box
              css={theme => ({
                flex: '0 1 100%',
                textAlign: 'left',
                marginBottom: 0,
                fontSize: theme.fontSizes.fs40,
                fontWeight: 'unset',
              })}
            >
              {reactHTMLParser(confirm.heading || withSmartling('mktg_ct_all_default_form_heading'))}
            </Box>
            <Box
              css={theme => ({
                marginTop: theme.space.s16,
                marginBottom: theme.space.s16,
                flex: '0 1 100%',
                textAlign: 'left',
                fontSize: theme.fontSizes.fs16,
              })}
            >
              {reactHTMLParser(confirm.body || withSmartling('mktg_ct_all_default_form_thankyou_message'))}
            </Box>
          </Box>
        )}
        {children && children}
      </React.Fragment>
    );
  }

  if (hasFormError) {
    return (
      <Box css={{ label: 'formError' }}>
        <Heading
          as="h2"
          css={theme => ({
            flex: '0 1 100%',
            textAlign: 'left',
            fontSize: theme.fontSizes.fs40,
            fontWeight: 'unset',
          })}
        >
          {withSmartling('mktg_mkto_fail_header')}
        </Heading>
        <Text
          css={theme => ({
            flex: '0 1 100%',
            textAlign: 'left',
            fontSize: theme.fontSizes.fs16,
          })}
        >
          {withSmartling('mktg_mkto_fail_adblocker')}
        </Text>
        <Text
          css={theme => ({
            flex: '0 1 100%',
            textAlign: 'left',
            fontSize: theme.fontSizes.fs16,
          })}
        >
          {withSmartling('mktg_mkto_fail_problem')}
        </Text>
      </Box>
    );
  }

  const addErrorToDataLayer = (errorMessage: string) => {
    sendGoogleEvent({
      name: 'mktoFormError',
      data: {
        marketo_formError: errorMessage,
        ...googleAnalyticsData,
      },
    });
  };

  const compileAccessibleErrors = (errors: ValidationErrors) => {
    const fieldErrors = Object.entries(errors || {})?.reduce((msg, [fieldId, errorMessage]) => {
      const errorField = form?.childrenMarketoFormField.find(field => field.marketoId === fieldId);

      return `${msg} ${errorField?.label}: ${errorMessage}`;
    }, '');

    return `${withSmartling('mktg_mkto_aria_validation')} ${fieldErrors}`;
  };

  const handleOnSubmit = async (values: any) => {
    const visitorData: Record<string, string | number> = {};
    const hiddenVisitorDataFields: Record<string, string> = {};
    const hiddenVisitorDataValues: Record<string, string> = {
      pageURL: getFormUrl(),
      queryString: window.location?.search || '',
      userAgentString: navigator.userAgent || 'unknown',
    };
    const hiddenLeadFormValues: Record<string, string | number> = {
      formURL__c: getFormUrl(),
      formProduct: getBeaconFormProduct(product),
      hasMoreThanTenCharacters: '',
    };

    if (textAreaIds.length > 0) {
      hiddenLeadFormValues.hasMoreThanTenCharacters = textAreaIds.some(id => values[id]?.length > 10)
        ? 'true'
        : 'false';
    }

    if (parsedQueryString?.['utm_content'] === 'enterpriseshield') {
      hiddenLeadFormValues.Detail_Lead_Source__c = 'Enterprise Shield - Contact Sales';
    }

    Object.keys(hiddenVisitorDataFields).map(key => {
      if (hiddenVisitorDataFields[key]) {
        hiddenVisitorDataValues[key] = hiddenVisitorDataFields[key];
      }
    });

    Object.keys(hiddenVisitorDataValues).map(key => {
      if (hiddenVisitorDataValues[key]) {
        visitorData[key] = hiddenVisitorDataValues[key];
      }
    });

    sessionStorage.removeItem(sessionKey);

    // Transform form values and send to Qualified to fire Meeting Booker experience if applicable
    if (shouldTriggerQualified) {
      const qualifiedObj = qualifiedFields.reduce<QualifiedVisitorObjectType>(
        (prev, curr) => {
          const fieldMatch = values[startCase(curr).replace(' ', '')];

          return !prev[curr as QualifiedFieldsType] || fieldMatch
            ? {
                ...prev,
                [curr]: fieldMatch,
              }
            : prev;
        },
        {
          // Default obj values will be overwritten if there is a direct match using Visitor Fields to index form values
          name: `${values['FirstName']} ${values['LastName']}`,
          title: values['edurole'],
          lead_source: values['Detail_Lead_Source__c'],
          status: values['LeadStatus'],
          conversion_page_url: getFormUrl(),
          description: values['MktoPersonNotes'],
        } as unknown as QualifiedVisitorObjectType,
      );

      window.qualified('saveFormData', qualifiedObj);
      window.qualified('emitFormFill', 'custom');
    }

    axios.interceptors.request.use(config => {
      // Send event to Tag Manager
      sendGoogleEvent({
        name: 'mktoFormSubmit',
        data: googleAnalyticsData,
      });

      // Send event to Beacon
      sendBeaconEvent({
        anonymousId: withCookie.get('lt-anonymous-id') || '',
        formId,
        formName: formName || '',
        formProduct: getBeaconFormProduct(product),
        formType: beaconFormType || 'sales',
        name: 'submittedMarketoForm',
        previousPageviewId: sessionStorage.getItem('previousPageviewId') || null,
        userId: Number(userId) || -1,
      });

      return config;
    });

    // Rewrite to Marketo API field names where applicable
    const leadFormFields = Object.entries(values).reduce<Record<string, any>>((result, [key, value]) => {
      const hiddenLeadFormFieldKeys = Object.keys(hiddenLeadFormValues);
      const fieldDefinition = form?.childrenMarketoFormField?.find(f => f.marketoId === key);

      if (fieldDefinition?.apiId) {
        const isCheckbox = fieldDefinition.dataType === 'checkbox';

        // format the checkbox to be a boolean value, instead of an array of strings.
        if (isCheckbox) {
          const isCheckboxValueArray = Array.isArray(value) && value.length;
          const currentValue = isCheckboxValueArray ? value[0] : value;

          return {
            ...result,
            [fieldDefinition.apiId]: currentValue,
          };
        }

        // add the hidden form fields with custom metadata
        if (hiddenLeadFormFieldKeys.includes(fieldDefinition.apiId)) {
          return {
            ...result,
            [fieldDefinition.apiId]: hiddenLeadFormValues[fieldDefinition.apiId],
          };
        }

        return {
          ...result,
          [fieldDefinition.apiId]: value,
        };
      }

      return result;
    }, {});

    const sendMarketoRequest = async () => {
      try {
        const token = await getToken();

        if (token) {
          leadFormFields['grecaptchaToken'] = token;
        }

        const response: AxiosResponse<Lucid.Marketo.CreateLeadAPIResponse> = await axios({
          url: 'https://marketo.lucid.co/createLead',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            formId: parseInt(formId!),
            input: [{ leadFormFields, visitorData, cookie: marketoMunchkinTracker }],
          },
        });

        // HTTP-Level errors  - https://developers.marketo.com/rest-api/error-codes/#http_level_errors
        if (response.status !== 200) {
          throw new Error(`Marketo HTTP Error: ${response.status} - ${response.statusText}`);
        }

        // Response-Level errors - https://developers.marketo.com/rest-api/error-codes/#response_level_errors
        if (!response.data.success || (response.data.errors && response.data.errors.length > 0)) {
          // Convert the 'errors' array into a string
          const errorMessages = response.data?.errors?.map(error => `${error.code}: ${error.message}`).join('; ');
          throw new Error(`Marketo Response Error(s)${errorMessages ? `: ${errorMessages}` : ''}`);
        }

        // Record-Level errors - https://developers.marketo.com/rest-api/error-codes/#record_level_errors
        const skippedLeads = response.data.result?.filter(lead => lead.status === 'skipped');

        if (skippedLeads && skippedLeads.length !== 0) {
          throw new Error(
            `Marketo createLead skipped ${skippedLeads.length} lead(s). Reasons: ${skippedLeads
              .map(lead => lead.reasons!.map(reason => reason.message).join(', '))
              .join('; ')}`,
          );
        }

        setHasFormSent(true);

        return response.data || {};
      } catch (error) {
        setHasFormSent(true);
        sessionStorage.setItem(sessionKey, JSON.stringify(values));

        sendBeaconEvent({
          anonymousId: withCookie.get('lt-anonymous-id') || '',
          formId,
          formName: formName || '',
          formProduct: getBeaconFormProduct(product),
          formType: beaconFormType || 'sales',
          name: 'failedMarketoFormSubmit',
          previousPageviewId: sessionStorage.getItem('previousPageviewId') || null,
          userId: Number(userId) || -1,
          errorField: error.message,
        });

        if (isBrowser()) {
          const skippedLeadError = error.message.startsWith('Marketo createLead skipped');

          addErrorToDataLayer(
            `Form: This Marketo form ${skippedLeadError ? 'submission was skipped' : 'could not be sent'}`,
          );

          splunkLogger({
            errorMessage: `Marketo-Form Error: ${error?.message || 'Unable to submit Marketo Form'}`,
            product: product || 'chart',
            location: window.location.href,
            severity: skippedLeadError ? 'Major' : 'Critical',
            datadogErrorTag: `marketing.marketo_form_submission_${skippedLeadError ? 'skipped' : 'failure'}`,
          });
        }

        await workato({
          data: {
            form: {
              formId: parseInt(formId!),
              input: [{ leadFormFields, visitorData, cookie: marketoMunchkinTracker }],
            },
          },
          errors: {
            message: error.message,
            code: error?.code,
            status: error?.status,
          },
        }).catch(() => {
          setHasWorkatoError(true);
        });
      }
    };
    const response = await sendMarketoRequest();

    if (response && response.success) {
      sessionStorage.removeItem(sessionKey);
    }
  };

  const handleOnValidate = (values: any) => {
    const errors: ValidationErrors = {};

    if (shouldRequireAtLeastOneCheckbox) {
      const hasAtLeastOneCheckboxChecked = requiredCheckboxList.some(checkbox => {
        const checkboxValue = values[checkbox];

        if (Array.isArray(checkboxValue)) {
          // some checkbox values can be a falsy value
          // so we must check the length of the array is greater than or equal to one.
          if (checkboxValue?.length) return checkboxValue[0];

          return undefined;
        }

        if (!checkboxValue) return undefined;

        return checkboxValue;
      });

      return hasAtLeastOneCheckboxChecked
        ? errors
        : { ...errors, requiredCheckbox: 'At least one checkbox is required' };
    }

    return errors;
  };

  const handleEnterpriseError = (messageText: string) => {
    sendBeaconEvent({
      name: 'shownErrorMessage',
      anonymousId: '',
      messageText: messageText,
      errorType: 'marketo form',
    });
  };

  const formatAutoComplete = (name: string) => {
    switch (name.toLowerCase()) {
      case 'firstname':
        return 'given-name';
      case 'lastname':
        return 'family-name';
      case 'name':
        return 'name';
      case 'email':
        return 'email';
      case 'phone':
        return 'tel-national';
      default:
        return 'off';
    }
  };

  const renderFormElements = (
    formFields: Lucid.Marketo.ChildrenFields[],
    formValues: any,
    form: FormApi<any, Partial<any>>,
  ) =>
    formFields.map(child => {
      const { dataType, marketoId, label, fieldMetaData, visibilityRules, instructions, hintText } = child;
      let visibilityFilterValues: Lucid.Marketo.Values[] | null | undefined = null;
      const currentDataType = dataType as Lucid.Marketo.DataType;
      const isCheckbox = currentDataType === 'checkbox';
      const isCheckboxes = currentDataType === 'checkboxes';
      const isCurrency = currentDataType === 'currency';
      const isDate = currentDataType === 'date';
      const isHTMLText = currentDataType === 'htmltext';
      const isHidden = currentDataType === 'hidden';
      const isRadioButtons = currentDataType === 'radioButtons';
      const isSelect = currentDataType === 'select';
      const isTextArea = currentDataType === 'textArea';
      const labelString = label || '';

      if (visibilityRules) {
        const { ruleType = '', rules } = visibilityRules;

        if (ruleType === 'show' || ruleType === 'hide') {
          const matchFound = rules?.find(({ subjectField, operator, values: ruleValues, picklistFilterValues }) => {
            visibilityFilterValues = picklistFilterValues;

            if (!subjectField) return undefined;

            if (operator === 'contains')
              return ruleValues?.some(ruleValue => formValues[subjectField]?.indexOf(ruleValue) !== -1);

            if (operator === 'is') return ruleValues?.some(ruleValue => formValues[subjectField] === ruleValue);

            if (operator === 'isNot') return ruleValues?.some(ruleValue => formValues[subjectField] !== ruleValue);

            return undefined;
          });

          // if a field doesn't match any condition, do not return a field.
          if ((ruleType === 'show' && !matchFound) || (ruleType === 'hide' && matchFound)) {
            // FIXME: use form api to add/remove this field based on matchFound/ruleType
            delete formValues[marketoId];

            return undefined;
          }
        }
      }
      const formattedLabel = labelString.endsWith(':') ? labelString.substring(0, labelString.length - 1) : labelString;
      const updatedLabel = formattedLabel.replace(hasHTMLTag, '');
      const filterValidationMessage = filterMarketoError.exec(child.validationMessage || '');
      const filteredValidationMessage = filterValidationMessage
        ? `${filterValidationMessage[1].trim()} ${filterValidationMessage[2]}`
        : child.validationMessage === 'This field is required.'
          ? `${child.label?.replace(/<[^>]*>/g, '')} is required.`
          : child.validationMessage;

      if (isHTMLText) {
        return (
          <Box
            key={marketoId}
            css={theme => [
              {
                label: 'htmlText',
                margin: `${theme.space.s24}px 0 ${theme.space.s32}px`,
                flex: '0 1 100%',
              },
              isFullWidth && {
                [theme.breakpoints.bp900.min]: {
                  flex: '0 1 100%',
                },
              },
            ]}
          >
            {reactHTMLParser(child.text || '')}
          </Box>
        );
      }

      if (isHidden) {
        return (
          <Field
            key={marketoId}
            isHidden
            component={InputField}
            customErrorMessage={filteredValidationMessage}
            hasDarkBackground={hasDarkBackground}
            id={marketoId}
            isDarkMode={isDarkMode}
            isFullWidth={isFullWidth}
            isRequired={child.required}
            label={updatedLabel}
            name={marketoId}
            parse={v => v}
            type="hidden"
            value={initialValues[marketoId]}
          />
        );
      }

      if (isDate) {
        return (
          <Field
            key={marketoId}
            ref={(refEl: HTMLInputElement) => formInputRef.current.push(refEl)}
            component={DateField}
            customErrorMessage={filteredValidationMessage}
            hasDarkBackground={hasDarkBackground}
            id={marketoId}
            isDarkMode={isDarkMode}
            isFullWidth={isFullWidth}
            isRequired={child.required}
            label={updatedLabel}
            name={marketoId}
            parse={v => v}
            placeholder={hintText || updatedLabel}
            type="date"
            validate={isRequired(child.required, filteredValidationMessage)}
            value={initialValues[marketoId]}
          />
        );
      }

      if (isCheckbox) {
        const handleSelectAllCheckboxes = (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.checked) {
            // This Field is used by the email team to unsubscribe the
            // user from all types of lists. They asked for this field
            // to check all other checkbox fields when selected.
            if (marketoId === 'Unsubscribed') {
              requiredCheckboxList.map(checkboxId => form.change(checkboxId, 'true'));
            }
          }
        };

        return (
          <Field
            key={marketoId}
            ref={(refEl: HTMLInputElement) => formInputRef.current.push(refEl)}
            component={CheckboxField}
            customErrorMessage={filteredValidationMessage}
            hasDarkBackground={hasDarkBackground}
            id={marketoId}
            instructions={instructions}
            isDarkMode={isDarkMode}
            isFullWidth={isFullWidth}
            isRequired={child.required}
            label={updatedLabel}
            name={marketoId}
            onClick={handleSelectAllCheckboxes}
            type="checkbox"
            validate={isRequired(child.required, filteredValidationMessage)}
          />
        );
      }

      if (isCheckboxes) {
        const hasCheckboxValues = !!fieldMetaData?.values?.length;
        const isCheckboxGroup = fieldMetaData?.values && fieldMetaData?.values?.length > 1;

        if (hasCheckboxValues) {
          return (
            <Fieldset key={marketoId} isRealFieldset={!!updatedLabel}>
              {updatedLabel && <Legend>{updatedLabel}</Legend>}
              <Field
                key={marketoId}
                component={CheckboxGroup}
                customErrorMessage={filteredValidationMessage}
                hasDarkBackground={hasDarkBackground}
                id={marketoId}
                instructions={isCheckboxGroup ? instructions : undefined}
                isDarkMode={isDarkMode}
                isFullWidth={isFullWidth}
                isRequired={child.required || false}
                label={updatedLabel}
                name={marketoId}
                shouldHideInstructions={isCheckboxGroup}
                validate={isRequired(child.required, filteredValidationMessage)}
                value={initialValues[marketoId]}
              >
                <FieldArray key={marketoId} name={marketoId}>
                  {() =>
                    fieldMetaData?.values?.map((checkbox, index) => (
                      <Field
                        key={`checkboxFieldArray-${marketoId}-${kebabCase(checkbox.label)}-${index}`}
                        ref={(refEl: HTMLInputElement) => formInputRef.current.push(refEl)}
                        component={CheckboxField}
                        hasDarkBackground={hasDarkBackground}
                        id={checkbox.label}
                        instructions={!isCheckboxGroup ? instructions : undefined}
                        isCheckboxGroup={isCheckboxGroup}
                        isDarkMode={isDarkMode}
                        isFullWidth={isFullWidth}
                        label={checkbox.label}
                        name={marketoId}
                        type="checkbox"
                        value={checkbox.value}
                      />
                    ))
                  }
                </FieldArray>
              </Field>
            </Fieldset>
          );
        }

        return (
          <Field
            key={marketoId}
            ref={(refEl: HTMLInputElement) => formInputRef.current.push(refEl)}
            component={CheckboxField}
            customErrorMessage={filteredValidationMessage}
            id={marketoId}
            instructions={instructions}
            isFullWidth={isFullWidth}
            isRequired={child.required}
            label={updatedLabel}
            name={marketoId}
            type="checkbox"
            validate={isRequired(child.required, filteredValidationMessage)}
            value={initialValues[marketoId]}
          />
        );
      }

      if (isRadioButtons) {
        return (
          <Field
            key={marketoId}
            component={RadioGroup}
            customErrorMessage={filteredValidationMessage}
            hasDarkBackground={hasDarkBackground}
            id={marketoId}
            isDarkMode={isDarkMode}
            isFullWidth={isFullWidth}
            isRequired={child.required || false}
            label={updatedLabel}
            name={marketoId}
            validate={isRequired(child.required, filteredValidationMessage)}
            value={initialValues[marketoId]}
          >
            <FieldArray name={marketoId}>
              {() =>
                fieldMetaData?.values?.map((radio, index) => (
                  <Field
                    key={`radioFieldArray-${marketoId}-${kebabCase(radio.label)}-${index}`}
                    ref={(refEl: HTMLInputElement) => formInputRef.current.push(refEl)}
                    component={RadioButtonField}
                    hasDarkBackground={hasDarkBackground}
                    id={radio.label}
                    isDarkMode={isDarkMode}
                    isFullWidth={isFullWidth}
                    isRequired={child.required}
                    label={radio.label}
                    name={marketoId}
                    type="radio"
                    value={radio.value}
                  />
                ))
              }
            </FieldArray>
          </Field>
        );
      }

      if (isSelect) {
        const options = visibilityFilterValues || fieldMetaData?.values;

        return (
          <Field
            key={marketoId}
            component={DropdownField}
            customErrorMessage={filteredValidationMessage}
            hasDarkBackground={hasDarkBackground}
            id={marketoId}
            isDarkMode={isDarkMode}
            isFullWidth={isFullWidth}
            isRequired={child.required}
            label={updatedLabel}
            name={marketoId}
            options={options}
            parse={v => v}
            type="select"
            validate={isSelectRequired(child.required, filteredValidationMessage)}
            value={initialValues[marketoId]}
            variant="form"
          />
        );
      }

      if (isTextArea) {
        textAreaIds.push(marketoId);

        return (
          <Field
            key={marketoId}
            ref={(refEl: HTMLTextAreaElement) => formInputRef.current.push(refEl)}
            isFullWidth
            aria-describedby={`textarea-${marketoId}`}
            as="textarea"
            component={InputField}
            customErrorMessage={filteredValidationMessage}
            hasDarkBackground={hasDarkBackground}
            id={marketoId}
            isDarkMode={isDarkMode}
            isRequired={child.required}
            label={updatedLabel}
            name={marketoId}
            parse={v => v}
            placeholder={hintText || updatedLabel}
            type="textarea"
            validate={isRequired(child.required, filteredValidationMessage)}
            value={initialValues[marketoId]}
          />
        );
      }

      if (isCurrency) {
        return (
          <Field
            key={marketoId}
            ref={(refEl: HTMLInputElement) => formInputRef.current.push(refEl)}
            autoComplete={formatAutoComplete(marketoId)}
            component={InputField}
            customErrorMessage={filteredValidationMessage}
            hasDarkBackground={hasDarkBackground}
            id={marketoId}
            isDarkMode={isDarkMode}
            isFullWidth={isFullWidth}
            isRequired={child.required}
            label={updatedLabel}
            min={0.0}
            name={marketoId}
            parse={v => v}
            placeholder={hintText || updatedLabel}
            type="number"
            validate={isRequired(child.required, filteredValidationMessage)}
            value={initialValues[marketoId]}
          />
        );
      }

      const dataTypeLookup = dataType as FieldTypeLookupKeys;
      const currentDataTypeLookup = typeLookup[dataTypeLookup];

      if (currentDataTypeLookup) {
        return (
          <Field
            key={marketoId}
            ref={(refEl: HTMLInputElement) => formInputRef.current.push(refEl)}
            aria-describedby={`input-${marketoId}`}
            autoComplete={formatAutoComplete(marketoId)}
            component={InputField}
            hasDarkBackground={hasDarkBackground}
            hasTwoColumns={formFields.length > 1}
            id={marketoId}
            isDarkMode={isDarkMode}
            isFullWidth={isFullWidth}
            isRequired={child.required}
            label={updatedLabel}
            min={0}
            name={marketoId}
            parse={v => v}
            placeholder={hintText || updatedLabel}
            type={currentDataTypeLookup}
            validate={composeValidators(
              isValidEmail(currentDataTypeLookup, filteredValidationMessage),
              isValidEnterpriseEmail(
                currentDataTypeLookup,
                isSalesForm,
                filteredValidationMessage,
                handleEnterpriseError,
              ),
              isValidPhoneNumber(currentDataTypeLookup, filteredValidationMessage),
              isRequired(child.required, filteredValidationMessage),
            )}
            value={initialValues[marketoId]}
          />
        );
      }
    });

  return (
    <Form
      {...rest}
      validateOnBlur
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
      }}
      onSubmit={handleOnSubmit}
      validate={handleOnValidate}
    >
      {({ dirty, errors, form, hasValidationErrors, handleSubmit, pristine, submitting, values, touched }) => {
        const isDisplayingErrors = Object.entries(touched || {}).some(
          ([fieldId, touched]) => Object.keys(errors || {}).includes(fieldId) && touched,
        );

        return (
          <form
            key={formId}
            ref={formElementRef}
            noValidate
            css={theme => isDarkMode && { background: theme.colors.coolGrey[100] }}
            // eslint-disable-next-line @stylistic/jsx-sort-props
            className={className}
            data-form-id={formId}
            method="post"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit}
          >
            {formId && (
              <FormStarted
                beaconEventData={{
                  name: 'initiatedMarketoForm',
                  formId,
                  formName: formName || '',
                  userId: Number(userId) || -1,
                  anonymousId: withCookie.get('lt-anonymous-id') || '',
                  previousPageviewId: sessionValues?.previousPageviewId || null,
                  formType: beaconFormType || 'sales',
                  formProduct: getBeaconFormProduct(product),
                }}
                googleAnalyticsData={googleAnalyticsData}
                isDirty={dirty}
              />
            )}
            {overrideTopText && !shouldHideTopText && (
              <Box
                css={theme => [
                  theme.shared.paragraphNestedLinkStyles(theme, 'primaryAlternate'),
                  {
                    flex: '0 1 100%',
                    marginBottom: theme.space.s32,
                  },
                ]}
              >
                {title && (
                  <Box
                    css={theme => ({
                      margin: 0,
                      marginBottom: theme.space.s16,
                    })}
                  >
                    {reactHTMLParser(title)}
                  </Box>
                )}
                {body && (
                  <Box
                    css={theme => ({
                      lineHeight: theme.lineHeights.lh1_35,
                    })}
                  >
                    {reactHTMLParser(body)}
                  </Box>
                )}
                {note && (
                  <Box
                    css={theme => ({
                      marginBottom: theme.space.s32,
                    })}
                  >
                    {reactHTMLParser(note)}
                  </Box>
                )}
              </Box>
            )}
            <Text
              aria-live="polite"
              css={theme => [
                { color: theme.colors.pink[80], fontStyle: 'italic' },
                (hasDarkBackground || isDarkMode) && {
                  color: theme.colors.white,
                },
              ]}
              data-testid="formErrorMessage-live"
              role="alert"
              variant="labelXS400"
            >
              {isDisplayingErrors ? withSmartling('mktg_form_errors_present') : ''}
            </Text>
            <Text
              css={theme => [
                {
                  fontSize: theme.fontSizes.fs12,
                  margin: `0 0 ${theme.space.s16}px 0`,
                },
                (hasDarkBackground || isDarkMode) && {
                  color: theme.colors.white,
                },
              ]}
              variant="labelXS400"
            >
              {withSmartling('mtkg_form_required')}
            </Text>
            {errors?.requiredCheckbox && (
              <Text
                as="span"
                css={theme => ({
                  color: theme.colors.pink[50],
                  textAlign: 'left',
                })}
                data-testid="fieldErrorMessage-unsubcribe-group"
                id="fieldErrorMessage-unsubcribe-group"
                variant="labelS400"
              >
                {withSmartling('mktg_unsub_checkbox_error')}
              </Text>
            )}
            {fieldGroups.map((fieldGroup, index) => {
              if (fieldGroup.fieldsetTitle) {
                return (
                  <Fieldset
                    key={`${fieldGroup.fieldsetTitle || 'defaultFieldGroup'}-${index + 1}`}
                    isRealFieldset={!!fieldGroup.fieldsetTitle}
                  >
                    {fieldGroup.fieldsetTitle && <Legend>{fieldGroup.fieldsetTitle}</Legend>}
                    {renderFormElements(fieldGroup.fields, values, form)}
                  </Fieldset>
                );
              }

              // TODO: remove isOneColumnLayout check when AB Test has run
              if (fieldGroup.fields.length > 1 && !isOneColumnLayout) {
                return (
                  <Box
                    key={`fieldGroudContainer-${index}`}
                    css={theme => ({
                      [theme.breakpoints.bp500.min]: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: theme.space.s8,
                      },
                    })}
                    data-testid="fieldGroupContainer"
                  >
                    {renderFormElements(fieldGroup.fields, values, form)}
                  </Box>
                );
              }

              return renderFormElements(fieldGroup.fields, values, form);
            })}
            <Box
              css={[
                {
                  flex: '0 1 100%',
                  textAlign: 'left',
                },
                (hasDarkBackground || isDarkMode) && {
                  color: 'white',
                },
              ]}
            >
              {willShowTerms && (
                <Box
                  css={theme => [
                    theme.shared.paragraphNestedLinkStyles(
                      theme,
                      hasDarkBackground || isDarkMode ? 'primaryInverse' : 'primaryAlternate',
                    ),
                    {
                      fontSize: theme.fontSizes.fs12,
                      margin: `${theme.space.s4}px 0 ${theme.space.s24}px`,
                    },
                  ]}
                >
                  {reactHTMLParser(
                    withSmartling('mtkg-submit-policy', {}, [
                      '<a href="https://lucid.co/privacy" target="_blank" rel="noopener noreferrer">{}</a>',
                    ]),
                  )}
                  {languageKey !== 'en' && (
                    <Text>{withSmartling('mktg_form_lang_disclaimer', { language: languageName })}</Text>
                  )}
                </Box>
              )}
              <AccessibleDescription id="form-errors" SRText={compileAccessibleErrors(errors)} />
              <Link
                asButton
                aria-describedby="form-errors"
                aria-disabled={!hasRecaptchaLoaded || submitting || pristine || hasValidationErrors}
                css={[
                  {
                    width: 'initial',
                    '&[aria-disabled="true"]': {
                      cursor: 'not-allowed',
                    },
                  },
                  hasFullWidthSubmit && {
                    width: '100%',
                    display: 'block',
                  },
                ]}
                data-testid="formSubmitButton"
                isDisabled={!hasRecaptchaLoaded || submitting || pristine || hasValidationErrors}
                onClick={() => {
                  // Gather client side validation message from browser and add to dataLayer
                  if (isBrowser() && errors) {
                    const errorName = errors?.name || '';

                    // remove potential PII from input added to dataLayer
                    if (errorName.toLowerCase() === 'email') {
                      const emailError = errors.Email || '';
                      const sanitizedErrorMessage = emailError.replace(values.Email.value, '{input value}');

                      addErrorToDataLayer(`Email: ${sanitizedErrorMessage}`);
                    }
                  }
                }}
                size="large"
                type="submit"
                variant={isDarkMode ? 'secondary' : 'primary'}
              >
                {submitting
                  ? withSmartling('mktg_generics_forms_submitting') || 'Submitting...'
                  : submitText || withSmartling('mktg_generics_forms_submit')}
              </Link>
            </Box>
            {hasWorkatoError && (
              <Box
                ref={alertRef}
                aria-label={`${withSmartling('mktg_workato_fail_header')}${withSmartling('mktg_workato_fail_header_2')}`}
                css={theme => ({
                  backgroundColor: theme.colors.red[5],
                  border: `solid ${theme.colors.red[60]} 2px`,
                  borderRadius: theme.radii.r16,
                  textAlign: 'left',
                  display: 'grid',
                  paddingLeft: theme.space.s24,
                  paddingRight: theme.space.s24,
                  columnGap: theme.space.s24,
                  gridTemplateColumns: `${theme.space.s24}px 1fr`,
                })}
                tabIndex={0}
              >
                <Icon css={{ display: 'flex', alignItems: 'center' }} sizing="responsive" type="Warning" />
                <Box>
                  <Text>{withSmartling('mktg_workato_fail_header')}</Text>
                  <Text>{withSmartling('mktg_workato_fail_header_2')}</Text>
                </Box>
              </Box>
            )}
          </form>
        );
      }}
    </Form>
  );
};

export { MarketoForm };
export default MarketoForm;
